import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MdDelete, MdFilterList, MdFilterListOff } from "react-icons/md";
import { RiEdit2Fill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import apiClient from "../services/apiClient";
import SearchIcon from "@mui/icons-material/Search";

import PageLoader from "../components/Loading/PageLoader";
import CustomButton from "../components/Button/CustomButton";
import CustomInputBox from "../components/Form/CustomInputBox";

import { setModal } from "../features/modal/ModalSlice";

const AiAgent = () => {
  const [aiAgents, setAIAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(true);

  const userInfo = useSelector((state) => state?.userInfo?.user);

  useEffect(() => {
    fetchAIAgent();
  }, []);

  const dispatch = useDispatch();

  const fetchAIAgent = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/agent-details/${userInfo?.id}`);
      setAIAgents(response?.data || []);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleAddAgent = () => {
    dispatch(setModal({ type: "ADD_AI_AGENT" }));
  };

  const handleEditRequest = (request) => {
    dispatch(setModal({ type: "EDIT_REQUESTS", data: request }));
  };

  const columns = useMemo(
    () => [
      { accessorKey: "company_domain", header: "Company" },
      { accessorKey: "no_of_phones_required", header: "Phones Required" },
      { accessorKey: "no_extensions_required", header: "Extensions Required" },
      {
        accessorKey: "assigned_phone_details",
        header: "Extensions Allocated",
        Cell: ({ cell }) => {
          const assigned_phone = cell.getValue();
          if (!assigned_phone) return "";
          const usernames = assigned_phone.map((phone) => phone.username).join(", ");
          return usernames;
        },
      },
      { accessorKey: "request_status", header: "Status" },
      { accessorKey: "created_at", header: "Date" },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: aiAgents,
    enableColumnOrdering: true,
    enableRowSelection: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton onClick={() => handleEditRequest(row?.original)}>
          <RiEdit2Fill />
        </IconButton>
        <IconButton>
          <MdDelete />
        </IconButton>
      </Box>
    ),
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#F5F6F8",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      },
    },
  });

  return (
    <>
      <div className="">
        {loading && <PageLoader text="Loading Ai Agents" />}

        <div className="pageTitle1 p-5">
          <h3>Ai Agents</h3>
          <p>{aiAgents?.length} Available</p>
        </div>

        <Box sx={{ padding: 2 }}>
          <div className="d-flex justify-content-between">
            <div className="w-50">
              <CustomInputBox
                icon={SearchIcon}
                placeholder="Search Request"
                type="text"
                backgroundColor={"#FFFFFF"}
                borderRadius="8px"
              />
            </div>

            <div className="d-flex justify-content-end gap-3 mb-4">
              <CustomButton
                text={(showFilter ? "Hide" : "Show") + " Filter"}
                color="secondary"
                iconPosition="left"
                icon={showFilter ? <MdFilterListOff /> : <MdFilterList />}
                onClick={handleShowFilter}
              />

              <CustomButton text={"Create Agent"} onClick={handleAddAgent} />
            </div>
          </div>

          {showFilter && (
            <div className="d-flex justify-content-start gap-3 mb-4">
              <Autocomplete
                disablePortal
                options={[]}
                sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                size="small"
                renderInput={(params) => <TextField {...params} label="Name" />}
              />

              <Autocomplete
                disablePortal
                options={[]}
                sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                size="small"
                renderInput={(params) => <TextField {...params} label="Number" />}
              />

              <CustomButton text={"Reset"} variant="text" color="inherit" />
            </div>
          )}

          <MaterialReactTable table={table} />
        </Box>
      </div>
    </>
  );
};

export default AiAgent;
